<template>
  <div id="root">
    <div class="page-header inner">
      <h1 data-test="title">Carrier Overview Dashboard</h1>
    </div>
    <FilterBar class="items-center">
      <GenericMultiFilter
        :options="coverageTypeOptions"
        v-model="selectedCoverageTypes"
        title="Coverage Type"
        chip-title-field="label"
        data-test="coverage-type-filter"
        @update:model-value="refreshData"
      />
      <GenericMultiFilter
        :options="carrierGroupOptions"
        v-model="selectedCarrierGroups"
        title="Carrier Group"
        chip-title-field="label"
        data-test="carrier-group-filter"
        @update:model-value="refreshData"
      />
      <template #filter-bar-right>
        <DateRangeSelector
          :initial-start-date="startDate"
          :initial-end-date="endDate"
          :on-apply="setDate"
        />
      </template>
    </FilterBar>
    <main class="inner">
      <div v-if="!loadingData" data-test="carrier-top-billable-chart">
        <fusioncharts type="bar2d" width="100%" height="400" dataFormat="json" :dataSource="topBillableDataSource"></fusioncharts>
      </div>
      <div v-if="!loadingData" data-test="carrier-top-commission-chart">
        <fusioncharts type="bar2d" width="100%" height="400" dataFormat="json" :dataSource="topCommissionDataSource"></fusioncharts>
      </div>
    </main>
  </div>
</template>

<script>
import FilterBar from '@/components/v2/FilterBar.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import DateRangeSelector from '@/components/v2/DateRangeSelector.vue';
import { carrierService, referenceService } from '@/services/api';
import { DateTime } from 'luxon';

const topBillableChart = {
  animation: '1',
  caption: 'Total Premium by Carrier Group',
  xAxisName: 'Carrier Group',
  yAxisName: 'Premium (Millions)',
  numberSuffix: 'M',
  theme: 'cacs',
  exportFilename: 'CACS Total Premium By Carrier Group'
};

const topCommissionChart = {
  animation: '1',
  caption: 'Total Commission by Carrier Group',
  xAxisName: 'Carrier Group',
  yAxisName: 'Commission (Millions)',
  numberSuffix: 'M',
  theme: 'cacs',
  exportFilename: 'CACS Total Commission By Carrier Group'
};

export default {
  name: 'CarrierOverviewDashboard',
  components: { FilterBar, GenericMultiFilter, DateRangeSelector },
  props: ['crumbs'],
  data: () => ({
    created: false,
    loadingFilters: true,
    loadingData: true,
    selectedCoverageTypes: [],
    selectedCarrierGroups: [],
    expandCoverageTypes: false,
    coverageTypes: [],
    carrierGroups: [],
    topBillableData: [],
    topCommissionData: [],
    startDate: DateTime.local().minus({ days: 366 }).toJSDate(),
    endDate: DateTime.local().minus({ days: 1 }).toJSDate()
  }),
  computed: {
    coverageTypeOptions() {
      return this.coverageTypes.map((ct) => ({ label: ct.text, value: ct.value }));
    },
    carrierGroupOptions() {
      return this.carrierGroups.map((cg) => ({ label: cg.text, value: cg.value }));
    },
    topBillableDataSource() {
      return { chart: topBillableChart, data: this.topBillableData };
    },
    topCommissionDataSource() {
      return { chart: topCommissionChart, data: this.topCommissionData };
    }
  },
  methods: {
    removeFilter(filter) {
      this.selectedCoverageTypes = this.selectedCoverageTypes.filter(c => c !== filter.value);
      void this.refreshData();
    },
    async refreshFilters() {
      this.loadingFilters = true;
      await Promise.all([this.loadCoverageTypes(), this.loadCarrierGroups()]);
      this.loadingFilters = false;
    },
    async refreshData() {
      if (this.created && this.loadingData) {
        return;
      }
      this.loadingData = true;
      await Promise.all([this.loadCarrierTopBillable(), this.loadCarrierTopCommission()]);
      this.loadingData = false;
    },
    setDate(datePayload) {
      if (datePayload.startDate) {
        this.startDate = datePayload.startDate;
      }
      if (datePayload.endDate) {
        this.endDate = datePayload.endDate;
      }
      this.refreshData();
    },
    async loadCoverageTypes() {
      try {
        this.coverageTypes = (await referenceService.coverageTypes()).map(ct => ({ text: ct.label, value: ct.label }));
      } catch (err) {
        console.error(err);
      }
    },
    async loadCarrierGroups() {
      try {
        this.carrierGroups = (await referenceService.carrierGroups()).map(ct => ({ text: ct.label, value: ct.label }));
      } catch (err) {
        console.error(err);
      }
    },
    async loadCarrierTopBillable() {
      try {
        const startDateTime = DateTime.fromJSDate(this.startDate);
        const endDateTime = DateTime.fromJSDate(this.endDate);
        this.topBillableData = await carrierService.findTopBillable(startDateTime, endDateTime, 10, this.selectedCoverageTypes, this.selectedCarrierGroups);
      } catch (err) {
        console.error(err);
      }
    },
    async loadCarrierTopCommission() {
      try {
        const startDateTime = DateTime.fromJSDate(this.startDate);
        const endDateTime = DateTime.fromJSDate(this.endDate);
        this.topCommissionData = await carrierService.findTopCommission(startDateTime, endDateTime, 10, this.selectedCoverageTypes, this.selectedCarrierGroups);
      } catch (err) {
        console.error(err);
      }
    }
  },
  created() {
    void this.refreshFilters();
    void this.refreshData();
    this.created = true;
  }
};
</script>

<style scoped>
.page-header {
  padding-bottom: 0.5rem;
}

main {
  display: flex;
  flex-flow: row wrap;
}

main > div {
  flex: 1;
  min-width: 500px;
  margin: 1rem 2rem;
}
</style>
